/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type { Database } from "../../../__definitions__/database-definitions";
import {
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";
import type { INTERVAL, ISO8601_DATE } from "../../../utils/iso8601";

export type EventKey = {
  key: string;
  title: string;
  calendar: boolean;
  date: ISO8601_DATE;
  library: boolean;
  repeat: INTERVAL | null | undefined;
  allow_initial: boolean;
  allow_repeats: boolean;
  has_schedule_event: boolean;
  number_of_repeats: number | undefined;
};

export class SupabaseEventService extends SupabaseService<"event"> {
  table = "event" as const;

  async getKeys(options?: { mustIncludeKey?: string; includeLibrary?: boolean }) {
    let query = this.client.from("event_key").select("*");
    if (!options?.includeLibrary) query = query.eq("library", false);

    return this.getAllFromSelection(
      null,
      options?.mustIncludeKey
        ? query.or(`deleted.is.false,key.eq.${options.mustIncludeKey}`)
        : query.or("deleted.is.false"),
      { order: [{ column: "key" }] },
    );
  }

  async getNumberOfRepeats(
    startDate: ISO8601_DATE,
    endDate: ISO8601_DATE,
    period: string,
    every: number,
  ): Promise<number> {
    const { data, error } = await this.client
      .rpc("get_number_of_event_repeats", {
        start_date: startDate,
        end_date: endDate,
        period,
        every,
      })
      .single();

    if (error) throw error;

    return data;
  }

  async getFinalOccurrence(
    startDate: ISO8601_DATE,
    interval: INTERVAL,
    number_of_repeats: number,
  ): Promise<ISO8601_DATE> {
    const { data, error } = await this.client
      .rpc("get_final_event_occurrence", {
        start_date_: startDate,
        interval_: interval,
        occurrences: number_of_repeats,
      })
      .single();

    if (error) throw error;

    return data;
  }
}

export class SupabaseEventsWithMomentsCalendarService extends SupabaseServiceView<"events_with_related_moments_calendar"> {
  table = "events_with_related_moments_calendar" as const;
}

export class SupabaseEventsWithMomentsPersonService extends SupabaseServiceView<"events_with_related_moments_person"> {
  table = "events_with_related_moments_person" as const;
}

export class SupabaseScheduleEventService extends SupabaseService<"schedule_event"> {
  table = "schedule_event" as const;

  async update(
    event_key: Database["public"]["Tables"]["schedule_event"]["Insert"]["event_key"],
    updateProps: Database["public"]["Tables"]["schedule_event"]["Update"],
  ) {
    return this.log_errors(
      await this.client.from(this.table).update(updateProps).match({ event_key }).select().single(),
    );
  }
}
